import { motion, useReducedMotion } from "framer-motion";

const EASE = [0.22, 1, 0.36, 1];

const PHASES = [
  {
    index: "01",
    kicker: "Decide",
    line: "Know what to choose.",
    tone: "light",
    steps: ["Learn", "Compare", "Understand", "Connect"],
  },
  {
    index: "02",
    kicker: "Realize",
    line: "Act with confidence.",
    tone: "dark",
    steps: ["Verify", "Access", "Repay", "Grow"],
  },
];

function Decisions() {
  const reduce = useReducedMotion();
  const fade = {
    hidden: reduce ? { opacity: 1, y: 0 } : { opacity: 0, y: 22 },
    show: {
      opacity: 1,
      y: 0,
      transition: reduce ? { duration: 0 } : { duration: 0.55, ease: EASE },
    },
  };

  return (
    <section
      id="decisions"
      className="relative overflow-hidden bg-white text-navy"
      aria-labelledby="decisions-heading"
    >
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute inset-0 bg-[radial-gradient(70%_50%_at_50%_0%,rgba(229,184,11,0.14),transparent_55%),radial-gradient(55%_40%_at_0%_100%,rgba(12,31,27,0.08),transparent_50%)]" />
        <div
          className="absolute inset-0 opacity-[0.4]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(12,31,27,0.07) 1px, transparent 1px), linear-gradient(90deg, rgba(12,31,27,0.07) 1px, transparent 1px)",
            backgroundSize: "28px 28px",
          }}
        />
        <div
          className="absolute inset-0 opacity-[0.22]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(12,31,27,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(12,31,27,0.08) 1px, transparent 1px)",
            backgroundSize: "84px 84px",
            backgroundPosition: "-1px -1px",
          }}
        />
        <div className="absolute left-[7%] top-[12%] h-24 w-24 border border-navy/10" />
        <div className="absolute right-[8%] bottom-[14%] h-16 w-16 border border-gold/40" />
      </div>

      <div className="relative mx-auto max-w-8xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
        <div className="grid items-end gap-8 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)] lg:gap-12">
          <motion.header
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={fade}
          >
            <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-navy/40">
              Informed customers
            </p>
            <h2
              id="decisions-heading"
              className="mt-4 font-semibold leading-[1.08] tracking-tight text-royal [font-size:clamp(2rem,5vw,3.75rem)]"
            >
              Success starts with
              <span className="mt-1 block">better decisions</span>
            </h2>
          </motion.header>

          <motion.p
            className="max-w-md font-semibold leading-snug tracking-tight text-navy/70 [font-size:clamp(1.15rem,2vw,1.45rem)] lg:justify-self-end lg:text-right"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={fade}
          >
            Every successful lending journey begins with an informed customer.
          </motion.p>
        </div>

        <p className="mt-10 text-[11px] font-semibold uppercase tracking-[0.22em] text-navy/35">
          SimFinance helps consumers
        </p>

        <div className="mt-4 grid gap-4 lg:grid-cols-2">
          {PHASES.map((phase, phaseIndex) => {
            const dark = phase.tone === "dark";
            const offset = phaseIndex * 4;

            return (
              <motion.article
                key={phase.kicker}
                className={`relative overflow-hidden border p-5 sm:p-6 ${
                  dark ? "border-navy bg-navy text-white" : "border-navy/10 bg-[#f3f7f5] text-navy"
                }`}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-80px" }}
                variants={fade}
              >
                {dark ? (
                  <div
                    className="pointer-events-none absolute inset-0 opacity-20"
                    style={{
                      backgroundImage:
                        "linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)",
                      backgroundSize: "24px 24px",
                    }}
                  />
                ) : null}

                <div className="relative flex items-end justify-between gap-4">
                  <div>
                    <p
                      className={`text-[11px] font-semibold uppercase tracking-[0.28em] ${
                        dark ? "text-gold" : "text-navy/40"
                      }`}
                    >
                      {phase.kicker}
                    </p>
                    <p className="mt-2 text-xl font-semibold tracking-tight sm:text-2xl">{phase.line}</p>
                  </div>
                  <span
                    className={`flex h-9 w-9 shrink-0 items-center justify-center text-[11px] font-semibold tracking-[0.12em] ${
                      dark ? "bg-gold text-navy" : "bg-navy text-white"
                    }`}
                  >
                    {phase.index}
                  </span>
                </div>

                <ol className="relative mt-6 grid grid-cols-2 gap-3">
                  {phase.steps.map((step, i) => (
                    <motion.li
                      key={step}
                      className={`flex min-h-[7.25rem] flex-col justify-between border p-4 ${
                        dark ? "border-white/15 bg-white/[0.04]" : "border-navy/10 bg-white"
                      }`}
                      initial={reduce ? false : { opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, amount: 0.4 }}
                      transition={
                        reduce
                          ? { duration: 0 }
                          : { duration: 0.4, delay: 0.05 * i, ease: EASE }
                      }
                    >
                      <span
                        className={`font-mono text-[11px] font-semibold tracking-[0.16em] ${
                          dark ? "text-gold" : "text-navy/30"
                        }`}
                      >
                        {String(offset + i + 1).padStart(2, "0")}
                      </span>
                      <span className="text-2xl font-semibold tracking-tight">{step}.</span>
                    </motion.li>
                  ))}
                </ol>
              </motion.article>
            );
          })}
        </div>

        <motion.p
          className="mt-8 max-w-2xl border-l-2 border-gold pl-5 text-base leading-relaxed text-navy/60 sm:text-lg"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={fade}
        >
          All within one intelligent ecosystem.
        </motion.p>
      </div>
    </section>
  );
}

export default Decisions;
